import { Box } from '@customink/pigment-react';
import {
  type ProjectItemDraft,
  useProduct,
  useProjectItems,
} from '@customink/project-service-react-components';
import { type ReactEventHandler, useState } from 'react';

import type { EditFormWcibmoType } from './hooks';
import { EditFormWcibmo, useProjectItemSizeValidator } from './hooks';
import ProjectItemAddForm from './ProjectItemAddForm';
import AddItemMinimumQuantity from '../Alerts/AddItemMinimumQuantity';
import MissingQuantity from '../Alerts/MissingQuantity';

import Modal from '@/components/Modal';
import { useCreateItem } from '@/hooks/ProjectItem';
import { pageViewTag } from '@/utils/metrics';

type ProjectItemAddModalProps = {
  draftProjectItem: ProjectItemDraft;
  onClose: ReactEventHandler;
  persistSelectedColor?: boolean;
};

export default function ProjectItemAddModal({
  draftProjectItem,
  onClose,
  persistSelectedColor = false,
}: ProjectItemAddModalProps) {
  const [createProjectItem] = useCreateItem();

  const { data: product } = useProduct({
    colorId: draftProjectItem.product?.colorId,
  });

  const { data: projectItems } = useProjectItems();

  const selectedColorIds = (product?.swatches || [])
    .filter((swatch) => {
      return projectItems?.some(
        (item) =>
          item.design?.cid === draftProjectItem.design?.cid &&
          item.product?.colorId === swatch.colorId,
      );
    })
    .map((swatch) => swatch.colorId);

  const firstUnselectedSwatch = (product?.swatches || []).find(
    (swatch) => !selectedColorIds.includes(swatch.colorId),
  );

  const initialDraftProjectItem: ProjectItemDraft = {
    ...draftProjectItem,
    product: {
      ...draftProjectItem.product,
      colorId: persistSelectedColor
        ? draftProjectItem.product?.colorId
        : firstUnselectedSwatch?.colorId,
    },
  };

  const [projectItemData, setProjectItemData] = useState<ProjectItemDraft>(
    initialDraftProjectItem,
  );
  const [wcibmoType, setWcibmoType] = useState<EditFormWcibmoType | null>(null);

  const validate = useProjectItemSizeValidator({
    colorId: projectItemData.product?.colorId,
    designCid: projectItemData.design?.cid,
    allowZeroQuantity: false,
  });

  const handleChange = (data: ProjectItemDraft) => {
    setProjectItemData(data);
  };

  const handleSubmit: ReactEventHandler = (event) => {
    const validationResult = validate(projectItemData);
    setWcibmoType(validationResult);
    if (validationResult) return false;

    pageViewTag('checkout/addcolor-submit');
    createProjectItem(projectItemData);
    onClose(event);
  };

  const buttonActions = {
    primary: {
      label: 'Add to Cart',
      onClick: handleSubmit,
    },
    secondary: {
      label: 'Cancel',
      onClick: onClose,
    },
  };

  const isHighColorMinimum =
    wcibmoType === EditFormWcibmo.HIGH_COLOR_MIN ||
    wcibmoType === EditFormWcibmo.HIGH_COLOR_MIN_OR_PRODUCT ||
    wcibmoType === EditFormWcibmo.HIGH_COLOR_MIN_OR_STYLE;

  return (
    <Modal
      buttonActions={buttonActions}
      disableMobileStyles
      onClose={onClose}
      open={true}
      scroll="body"
      title="Choose Color and Sizes"
    >
      <ProjectItemAddForm
        draftProjectItem={projectItemData}
        onChange={handleChange}
      />
      {wcibmoType === EditFormWcibmo.MISSING_QUANTITY && (
        <Box marginTop="16px">
          <MissingQuantity />
        </Box>
      )}
      {(wcibmoType === EditFormWcibmo.BELOW_COLOR_MIN ||
        wcibmoType === EditFormWcibmo.BELOW_ORDER_MIN ||
        wcibmoType === EditFormWcibmo.BELOW_MIN_WITH_ALTERNATIVE ||
        isHighColorMinimum) && (
        <Box marginTop="16px">
          <AddItemMinimumQuantity
            isHighColorDesign={isHighColorMinimum}
            product={product}
            selectedColorId={projectItemData.product?.colorId}
          />
        </Box>
      )}
    </Modal>
  );
}
